import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  BarChart3, Users, Loader2, Plus, Sparkles, Target, Trash2, RefreshCw,
  CheckCircle2, Clock, AlertTriangle, ArrowUpRight, Brain, Zap, Play,
  Pause, Calendar, ListChecks, TrendingUp, Award, Activity,
} from "lucide-react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

let ApexChart = null;
try { ApexChart = require("react-apexcharts").default; } catch (e) { /* not available */ }

const SUB_TABS = [
  { key: "overview", label: "Tổng quan", icon: BarChart3 },
  { key: "tasks", label: "Tasks", icon: ListChecks },
  { key: "kpi", label: "KPI", icon: Award },
  { key: "workflows", label: "Workflows", icon: Zap },
  { key: "suggestions", label: "AI Gợi ý", icon: Brain },
];

const PRIORITY_COLORS = {
  urgent: "bg-red-500/20 text-red-400",
  high: "bg-amber-500/20 text-amber-400",
  normal: "bg-sky-500/20 text-sky-400",
  low: "bg-slate-500/20 text-slate-400",
};
const PRIORITY_LABELS = { urgent: "Khẩn cấp", high: "Cao", normal: "Bình thường", low: "Thấp" };

const STATUS_COLORS = {
  draft: "bg-purple-500/20 text-purple-400",
  pending: "bg-amber-500/20 text-amber-400",
  in_progress: "bg-sky-500/20 text-sky-400",
  completed: "bg-emerald-500/20 text-emerald-400",
  cancelled: "bg-slate-500/20 text-slate-400",
};
const STATUS_LABELS = { draft: "Nháp (AI)", pending: "Chờ", in_progress: "Đang làm", completed: "Xong", cancelled: "Huỷ" };

function fmtDate(d) { return d ? d.slice(5, 10).replace("-", "/") : ""; }

export default function OperationsPanel() {
  const [subTab, setSubTab] = useState("overview");
  return (
    <div className="flex-1 overflow-hidden flex flex-col" data-testid="operations-panel">
      <div className="flex gap-1 px-4 py-2 border-b border-[#1e293b] bg-[#0a0f1a] overflow-x-auto">
        {SUB_TABS.map(t => (
          <button key={t.key} onClick={() => setSubTab(t.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium whitespace-nowrap transition-colors ${subTab === t.key ? "bg-[#1e293b] text-lime-400" : "text-slate-500 hover:text-slate-300"}`}
            data-testid={`ops-tab-${t.key}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto">
        {subTab === "overview" && <OverviewTab />}
        {subTab === "tasks" && <TasksTab />}
        {subTab === "kpi" && <KPITab />}
        {subTab === "workflows" && <WorkflowsTab />}
        {subTab === "suggestions" && <SuggestionsTab />}
      </div>
    </div>
  );
}

// ==================== OVERVIEW ====================
function OverviewTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/operations/overview").then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-lime-400 animate-spin" /></div>;
  if (!data) return null;

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4" data-testid="ops-overview">
      <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><Activity className="w-5 h-5 text-lime-400" /> AI COO - Quản lý vận hành</h2>

      {/* KPI Cards — Valex Sale Card Style */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "TONG TASKS", value: data.total_tasks, gradient: "from-[#4680ff] to-[#5b8def]", sparkData: data.timeline?.map(t => t.created) || [] },
          { label: "HOAN THANH", value: `${data.completed} (${data.completion_rate}%)`, gradient: "from-[#05c3a6] to-[#22d6b4]", sparkData: data.timeline?.map(t => t.completed) || [] },
          { label: "DANG LAM", value: data.in_progress, gradient: "from-[#00b9ff] to-[#38bdf8]", sparkData: data.timeline?.map((t, i) => i % 2 === 0 ? 1 : 0) || [] },
          { label: "QUA HAN", value: data.overdue, gradient: "from-[#fc5286] to-[#fd6d8e]", sparkData: data.timeline?.map(() => Math.random() * 2) || [] },
        ].map((m, i) => (
          <div key={i} className={`relative overflow-hidden rounded-xl bg-gradient-to-b ${m.gradient} p-4 min-h-[140px] flex flex-col justify-between shadow-lg`}>
            <p className="text-[10px] font-bold text-white/80 tracking-wider uppercase">{m.label}</p>
            <p className="text-2xl font-bold text-white">{m.value}</p>
            {/* Sparkline */}
            {ApexChart && m.sparkData.length > 0 && (
              <div className="absolute bottom-0 left-0 right-0 opacity-40">
                <ApexChart type="area" height={50} width="100%"
                  options={{
                    chart: { sparkline: { enabled: true }, animations: { enabled: true, easing: 'easeinout', speed: 1000 } },
                    stroke: { curve: 'smooth', width: 2 },
                    fill: { type: 'gradient', gradient: { opacityFrom: 0.5, opacityTo: 0, stops: [0, 100] } },
                    colors: ['#ffffff'],
                    tooltip: { enabled: false },
                  }}
                  series={[{ data: m.sparkData.slice(-14) }]}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Alerts */}
      {data.alerts?.length > 0 && (
        <div className="space-y-1.5">
          {data.alerts.map((a, i) => (
            <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${a.type === "danger" ? "bg-red-500/5 border-red-500/20" : "bg-amber-500/5 border-amber-500/20"}`}>
              <AlertTriangle className={`w-3.5 h-3.5 ${a.type === "danger" ? "text-red-400" : "text-amber-400"}`} />
              <span className="text-xs text-slate-300">{a.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* Timeline Chart — ApexCharts Valex Style */}
      {data.timeline?.length > 0 && (
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
          <p className="text-xs text-slate-400 mb-3">Hoạt động 14 ngày</p>
          {ApexChart ? (
            <ApexChart
              type="bar"
              height={160}
              options={{
                chart: { toolbar: { show: false }, animations: { enabled: true, easing: 'easeinout', speed: 600 } },
                plotOptions: { bar: { borderRadius: 3, columnWidth: '50%' } },
                colors: ['#6366f1', '#22c03c'],
                xaxis: { categories: data.timeline.map(t => t.date.slice(5)), labels: { style: { fontSize: '9px' } } },
                yaxis: { labels: { style: { fontSize: '9px' } } },
                grid: { borderColor: 'var(--default-border, #1e293b)', strokeDashArray: 3 },
                legend: { position: 'bottom', fontSize: '10px' },
                tooltip: { theme: 'dark' },
                dataLabels: { enabled: false },
              }}
              series={[
                { name: 'Tạo mới', data: data.timeline.map(t => t.created) },
                { name: 'Hoàn thành', data: data.timeline.map(t => t.completed) },
              ]}
            />
          ) : (
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={data.timeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 9 }} tickFormatter={v => v.slice(5)} />
                <YAxis tick={{ fill: "#64748b", fontSize: 9 }} />
                <Tooltip contentStyle={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 8, fontSize: 11, color: "#e2e8f0" }} />
                <Bar dataKey="created" name="Tạo mới" fill="#6366f1" radius={[3, 3, 0, 0]} />
                <Bar dataKey="completed" name="Hoàn thành" fill="#10b981" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      )}

      {/* Department Stats */}
      {data.department_stats?.length > 0 && (
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
          <p className="text-xs text-slate-400 mb-2">Theo phòng ban</p>
          <div className="space-y-1.5">
            {data.department_stats.map((d, i) => (
              <div key={i} className="flex items-center justify-between py-1.5 border-b border-[#334155]/30 last:border-0">
                <span className="text-xs text-slate-300 capitalize">{d.department || "Khác"}</span>
                <div className="flex gap-3 text-[10px]">
                  <span className="text-slate-400">{d.total} tasks</span>
                  <span className="text-emerald-400">{d.completed} xong</span>
                  {d.overdue > 0 && <span className="text-red-400">{d.overdue} quá hạn</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2 text-[10px] text-slate-500">
        <span>{data.staff_count} nhân viên</span>
        <span>•</span>
        <span>{data.active_workflows} workflows hoạt động</span>
        <span>•</span>
        <span>{data.drafts} tasks chờ duyệt</span>
      </div>
    </div>
  );
}

// ==================== TASKS ====================
function TasksTab() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [filter, setFilter] = useState({ status: "", priority: "" });
  const [form, setForm] = useState({ title: "", description: "", department: "", assigned_to: "", priority: "normal", deadline: "" });
  const [gantt, setGantt] = useState([]);
  const [showGantt, setShowGantt] = useState(false);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filter.status) params.append("status", filter.status);
    if (filter.priority) params.append("priority", filter.priority);
    try { const { data } = await api.get(`/operations/tasks?${params}`); setTasks(data.tasks || []); } catch {}
    finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const fetchGantt = async () => {
    try { const { data } = await api.get("/operations/gantt"); setGantt(data.items || []); setShowGantt(true); } catch {}
  };

  const generate = async () => {
    setGenerating(true);
    try { const { data } = await api.post("/operations/tasks/ai-generate"); toast.success(data.message); fetchTasks(); }
    catch { toast.error("Lỗi"); }
    finally { setGenerating(false); }
  };

  const submit = async () => {
    if (!form.title) { toast.error("Nhập tiêu đề"); return; }
    try { await api.post("/operations/tasks", form); toast.success("Đã tạo"); setAddOpen(false); setForm({ title: "", description: "", department: "", assigned_to: "", priority: "normal", deadline: "" }); fetchTasks(); }
    catch { toast.error("Lỗi"); }
  };

  const updateStatus = async (id, status) => {
    try { await api.put(`/operations/tasks/${id}`, { status }); fetchTasks(); } catch {}
  };

  const approve = async (id) => {
    try { await api.post(`/operations/tasks/${id}/approve`); toast.success("Đã duyệt"); fetchTasks(); } catch {}
  };

  const approveAll = async () => {
    try { const { data } = await api.post("/operations/tasks/approve-all"); toast.success(data.message); fetchTasks(); } catch {}
  };

  const evaluate = async (id) => {
    try { const { data } = await api.post(`/operations/tasks/${id}/evaluate`); toast.success(`Điểm: ${data.score}/100`); fetchTasks(); } catch { toast.error("Lỗi"); }
  };

  const del = async (id) => { try { await api.delete(`/operations/tasks/${id}`); fetchTasks(); } catch {} };

  const drafts = tasks.filter(t => t.status === "draft");

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4" data-testid="tasks-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><ListChecks className="w-5 h-5 text-lime-400" /> Quản lý Tasks</h2>
        <div className="flex gap-2">
          <Button onClick={fetchGantt} variant="outline" className="border-[#334155] text-slate-300 h-8 text-xs gap-1"><Calendar className="w-3.5 h-3.5" /> Gantt</Button>
          <Button onClick={generate} disabled={generating} variant="outline" className="border-[#334155] text-slate-300 h-8 text-xs gap-1">
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} AI Tạo task
          </Button>
          <Button onClick={() => setAddOpen(true)} className="bg-lime-500 hover:bg-lime-400 text-[#0f172a] h-8 text-xs gap-1"><Plus className="w-3.5 h-3.5" /> Thêm</Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        <select value={filter.status} onChange={e => setFilter({...filter, status: e.target.value})} className="bg-[#1e293b] border border-[#334155] rounded-md px-2 py-1 text-xs text-slate-300">
          <option value="">Tất cả trạng thái</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={filter.priority} onChange={e => setFilter({...filter, priority: e.target.value})} className="bg-[#1e293b] border border-[#334155] rounded-md px-2 py-1 text-xs text-slate-300">
          <option value="">Tất cả ưu tiên</option>
          {Object.entries(PRIORITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      {/* Drafts waiting approval */}
      {drafts.length > 0 && (
        <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-purple-400 font-medium">{drafts.length} tasks AI đề xuất chờ duyệt</p>
            <Button onClick={approveAll} size="sm" className="bg-purple-500/20 text-purple-400 h-6 text-[10px] gap-1"><CheckCircle2 className="w-3 h-3" /> Duyệt tất cả</Button>
          </div>
          {drafts.slice(0, 3).map(t => (
            <div key={t.id} className="flex items-center justify-between py-1.5 border-b border-purple-500/10 last:border-0">
              <div className="flex items-center gap-2">
                <Sparkles className="w-3 h-3 text-purple-400" />
                <span className="text-xs text-slate-300">{t.title}</span>
                <span className="text-[9px] text-slate-600">{t.ai_reason}</span>
              </div>
              <button onClick={() => approve(t.id)} className="text-[10px] text-purple-400 hover:text-purple-300">Duyệt</button>
            </div>
          ))}
        </div>
      )}

      {/* Task List */}
      {loading ? <Loader2 className="w-5 h-5 text-lime-400 animate-spin mx-auto" /> :
      tasks.filter(t => t.status !== "draft").length === 0 ? <p className="text-slate-500 text-center py-8 text-sm">Chưa có task. Bấm "AI Tạo task" hoặc "Thêm".</p> :
      <div className="space-y-1.5">
        {tasks.filter(t => t.status !== "draft").map(t => (
          <div key={t.id} className="bg-[#1e293b] border border-[#334155] rounded-lg px-3 py-2.5" data-testid={`task-${t.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Badge className={`text-[8px] border-0 ${PRIORITY_COLORS[t.priority]}`}>{PRIORITY_LABELS[t.priority]}</Badge>
                  <Badge className={`text-[8px] border-0 ${STATUS_COLORS[t.status]}`}>{STATUS_LABELS[t.status]}</Badge>
                  {t.ai_generated && <Sparkles className="w-3 h-3 text-purple-400" />}
                  {t.department && <span className="text-[9px] text-slate-600 capitalize">{t.department}</span>}
                  {t.linked_experiment_title && <Badge className="text-[7px] border-0 bg-indigo-500/20 text-indigo-400">TN: {t.linked_experiment_title}</Badge>}
                </div>
                <p className="text-xs text-slate-200 font-medium">{t.title}</p>
                {t.description && <p className="text-[10px] text-slate-500 mt-0.5">{t.description}</p>}
                <div className="flex gap-3 mt-1 text-[9px] text-slate-600">
                  {t.assigned_to && <span>Giao: {t.assigned_to}</span>}
                  {t.deadline && <span>Hạn: {fmtDate(t.deadline)}</span>}
                  {t.ai_score !== null && t.ai_score !== undefined && <span className="text-lime-400">Điểm AI: {t.ai_score}</span>}
                </div>
              </div>
              <div className="flex gap-1">
                {t.status === "pending" && <button onClick={() => updateStatus(t.id, "in_progress")} className="text-sky-400/60 hover:text-sky-400"><Play className="w-3.5 h-3.5" /></button>}
                {t.status === "in_progress" && <button onClick={() => updateStatus(t.id, "completed")} className="text-emerald-400/60 hover:text-emerald-400"><CheckCircle2 className="w-3.5 h-3.5" /></button>}
                {t.status === "completed" && !t.ai_score && <button onClick={() => evaluate(t.id)} className="text-purple-400/60 hover:text-purple-400" title="AI đánh giá"><Brain className="w-3.5 h-3.5" /></button>}
                <button onClick={() => del(t.id)} className="text-red-400/40 hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>}

      {/* Gantt Chart Modal */}
      <Dialog open={showGantt} onOpenChange={setShowGantt}>
        <DialogContent className="bg-[#1e293b] border-[#334155] text-slate-200 max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-lime-400 flex items-center gap-2"><Calendar className="w-5 h-5" /> Gantt Chart</DialogTitle></DialogHeader>
          <GanttChart items={gantt} />
        </DialogContent>
      </Dialog>

      {/* Add Task Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-[#1e293b] border-[#334155] text-slate-200 max-w-sm">
          <DialogHeader><DialogTitle className="text-lime-400">Tạo task mới</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div><Label className="text-slate-400 text-[10px]">Tiêu đề *</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-0.5 h-8 text-xs" /></div>
            <div><Label className="text-slate-400 text-[10px]">Mô tả</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-0.5 h-8 text-xs" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-slate-400 text-[10px]">Phòng ban</Label><Input value={form.department} onChange={e => setForm({...form, department: e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-0.5 h-8 text-xs" placeholder="marketing" /></div>
              <div><Label className="text-slate-400 text-[10px]">Giao cho</Label><Input value={form.assigned_to} onChange={e => setForm({...form, assigned_to: e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-0.5 h-8 text-xs" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-slate-400 text-[10px]">Ưu tiên</Label>
                <select value={form.priority} onChange={e => setForm({...form, priority: e.target.value})} className="w-full mt-0.5 bg-[#0f172a] border border-[#334155] rounded-md px-3 py-2 text-xs text-slate-200">
                  {Object.entries(PRIORITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div><Label className="text-slate-400 text-[10px]">Deadline</Label><Input type="date" value={form.deadline} onChange={e => setForm({...form, deadline: e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-0.5 h-8 text-xs" /></div>
            </div>
            <Button onClick={submit} className="w-full bg-lime-500 hover:bg-lime-400 text-[#0f172a] font-semibold h-9 text-xs">Tạo task</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== GANTT CHART ====================
function GanttChart({ items }) {
  if (!items || items.length === 0) return <p className="text-sm text-slate-500 text-center py-8">Chưa có task có deadline để hiển thị Gantt.</p>;

  // Calculate date range
  const dates = items.flatMap(i => [i.start, i.end]).filter(Boolean).sort();
  const minDate = dates[0] || new Date().toISOString().slice(0, 10);
  const maxDate = dates[dates.length - 1] || new Date().toISOString().slice(0, 10);
  const startD = new Date(minDate);
  const endD = new Date(maxDate);
  const totalDays = Math.max(Math.ceil((endD - startD) / 86400000), 7);

  const dayToPercent = (dateStr) => {
    const d = new Date(dateStr);
    return Math.max(0, Math.min(100, ((d - startD) / 86400000 / totalDays) * 100));
  };

  return (
    <div className="space-y-1.5 mt-2">
      {/* Date header */}
      <div className="flex justify-between text-[9px] text-slate-600 px-24">
        <span>{minDate.slice(5)}</span>
        <span>{maxDate.slice(5)}</span>
      </div>
      {items.map((item, i) => {
        const left = dayToPercent(item.start);
        const right = dayToPercent(item.end);
        const width = Math.max(right - left, 2);
        const barColor = item.status === "completed" ? "bg-emerald-500" : item.status === "in_progress" ? "bg-sky-500" : item.priority === "urgent" ? "bg-red-500" : "bg-slate-500";
        return (
          <div key={i} className="flex items-center gap-2">
            <div className="w-20 text-[9px] text-slate-400 truncate text-right">{item.title}</div>
            <div className="flex-1 h-5 bg-[#0f172a] rounded relative">
              <div className={`absolute top-0.5 h-4 rounded ${barColor}`} style={{ left: `${left}%`, width: `${width}%` }}>
                <span className="text-[7px] text-white px-1 leading-4 truncate block">{item.assigned_to || ""}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ==================== KPI ====================
function KPITab() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("month");

  useEffect(() => {
    setLoading(true);
    api.get(`/operations/kpi?period=${period}`).then(r => { setData(r.data.kpi || []); setLoading(false); }).catch(() => setLoading(false));
  }, [period]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-lime-400 animate-spin" /></div>;

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4" data-testid="kpi-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><Award className="w-5 h-5 text-lime-400" /> KPI Nhân viên</h2>
        <div className="flex gap-1">
          {[["week", "Tuần"], ["month", "Tháng"]].map(([k, l]) => (
            <button key={k} onClick={() => setPeriod(k)} className={`px-2.5 py-1 rounded text-[10px] ${period === k ? "bg-lime-500/20 text-lime-400" : "text-slate-500"}`}>{l}</button>
          ))}
        </div>
      </div>

      {data.length === 0 ? <p className="text-slate-500 text-center py-8 text-sm">Chưa có dữ liệu KPI. Giao task cho nhân viên để bắt đầu.</p> :
      <div className="space-y-2">
        {/* Bar Chart */}
        {data.length > 1 && (
          <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="name" tick={{ fill: "#64748b", fontSize: 9 }} />
                <YAxis tick={{ fill: "#64748b", fontSize: 9 }} domain={[0, 100]} />
                <Tooltip contentStyle={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 8, fontSize: 11, color: "#e2e8f0" }} />
                <Bar dataKey="overall_score" name="Điểm tổng" fill="#84cc16" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Staff Cards */}
        {data.map((s, i) => (
          <div key={i} className="bg-[#1e293b] border border-[#334155] rounded-xl p-4" data-testid={`kpi-card-${i}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${s.overall_score >= 80 ? "bg-emerald-500/20 text-emerald-400" : s.overall_score >= 50 ? "bg-amber-500/20 text-amber-400" : "bg-red-500/20 text-red-400"}`}>
                  {i + 1}
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-200">{s.name}</p>
                  <p className="text-[9px] text-slate-500">{s.role} {s.department && `• ${s.department}`}</p>
                </div>
              </div>
              <div className={`text-2xl font-bold ${s.overall_score >= 80 ? "text-emerald-400" : s.overall_score >= 50 ? "text-amber-400" : "text-red-400"}`}>
                {s.overall_score}
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2 text-center">
              <div className="bg-[#0f172a] rounded-lg p-1.5">
                <p className="text-[8px] text-slate-600">Tasks</p>
                <p className="text-xs font-bold text-slate-300">{s.completed}/{s.total_tasks}</p>
              </div>
              <div className="bg-[#0f172a] rounded-lg p-1.5">
                <p className="text-[8px] text-slate-600">Hoàn thành</p>
                <p className="text-xs font-bold text-emerald-400">{s.completion_rate}%</p>
              </div>
              <div className="bg-[#0f172a] rounded-lg p-1.5">
                <p className="text-[8px] text-slate-600">Đúng hạn</p>
                <p className="text-xs font-bold text-sky-400">{s.on_time_rate}%</p>
              </div>
              <div className="bg-[#0f172a] rounded-lg p-1.5">
                <p className="text-[8px] text-slate-600">Chất lượng</p>
                <p className="text-xs font-bold text-lime-400">{s.quality_score}</p>
              </div>
            </div>
            {s.overdue > 0 && <p className="text-[10px] text-red-400 mt-2">Quá hạn: {s.overdue} tasks</p>}
          </div>
        ))}
      </div>}
    </div>
  );
}

// ==================== WORKFLOWS ====================
function WorkflowsTab() {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", trigger: "", condition: "", action: "", department: "" });

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/operations/workflows"); setWorkflows(data.workflows || []); } catch {}
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const seedDefaults = async () => {
    try { const { data } = await api.post("/operations/workflows/seed-defaults"); toast.success(data.message); fetch(); } catch {}
  };

  const toggle = async (id) => {
    try { await api.put(`/operations/workflows/${id}/toggle`); fetch(); } catch {}
  };

  const del = async (id) => { try { await api.delete(`/operations/workflows/${id}`); fetch(); } catch {} };

  const submit = async () => {
    if (!form.name || !form.trigger || !form.action) { toast.error("Nhập đủ thông tin"); return; }
    try { await api.post("/operations/workflows", form); toast.success("Đã tạo"); setAddOpen(false); setForm({ name: "", trigger: "", condition: "", action: "", department: "" }); fetch(); } catch {}
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4" data-testid="workflows-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><Zap className="w-5 h-5 text-lime-400" /> Workflow Automation</h2>
        <div className="flex gap-2">
          <Button onClick={seedDefaults} variant="outline" className="border-[#334155] text-slate-300 h-8 text-xs gap-1"><RefreshCw className="w-3.5 h-3.5" /> Mặc định</Button>
          <Button onClick={() => setAddOpen(true)} className="bg-lime-500 hover:bg-lime-400 text-[#0f172a] h-8 text-xs gap-1"><Plus className="w-3.5 h-3.5" /> Thêm</Button>
        </div>
      </div>

      {loading ? <Loader2 className="w-5 h-5 text-lime-400 animate-spin mx-auto" /> :
      workflows.length === 0 ? <p className="text-slate-500 text-center py-8 text-sm">Chưa có workflow. Bấm "Mặc định" để tạo sẵn.</p> :
      <div className="space-y-2">
        {workflows.map(w => (
          <div key={w.id} className={`bg-[#1e293b] border rounded-xl p-3 ${w.is_active ? "border-[#334155]" : "border-[#334155]/50 opacity-60"}`}>
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Zap className={`w-3.5 h-3.5 ${w.is_active ? "text-lime-400" : "text-slate-600"}`} />
                  <p className="text-xs font-medium text-slate-200">{w.name}</p>
                  {w.department && <span className="text-[9px] text-slate-600 capitalize">{w.department}</span>}
                </div>
                <div className="flex gap-4 text-[10px] text-slate-500">
                  <span>Trigger: <span className="text-slate-400">{w.trigger}</span></span>
                  <span>Action: <span className="text-slate-400">{w.action}</span></span>
                </div>
                {w.condition && <p className="text-[9px] text-slate-600 mt-0.5">Điều kiện: {w.condition}</p>}
              </div>
              <div className="flex items-center gap-2">
                {w.trigger_count > 0 && <span className="text-[9px] text-slate-600">{w.trigger_count}x</span>}
                <button onClick={() => toggle(w.id)} className={w.is_active ? "text-lime-400" : "text-slate-600"}>
                  {w.is_active ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
                </button>
                <button onClick={() => del(w.id)} className="text-red-400/40 hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-[#1e293b] border-[#334155] text-slate-200 max-w-sm">
          <DialogHeader><DialogTitle className="text-lime-400">Tạo Workflow</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div><Label className="text-slate-400 text-[10px]">Tên *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-0.5 h-8 text-xs" /></div>
            <div><Label className="text-slate-400 text-[10px]">Trigger *</Label><Input value={form.trigger} onChange={e => setForm({...form, trigger: e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-0.5 h-8 text-xs" placeholder="low_booking" /></div>
            <div><Label className="text-slate-400 text-[10px]">Điều kiện</Label><Input value={form.condition} onChange={e => setForm({...form, condition: e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-0.5 h-8 text-xs" /></div>
            <div><Label className="text-slate-400 text-[10px]">Action *</Label><Input value={form.action} onChange={e => setForm({...form, action: e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-0.5 h-8 text-xs" placeholder="Tạo task marketing" /></div>
            <div><Label className="text-slate-400 text-[10px]">Phòng ban</Label><Input value={form.department} onChange={e => setForm({...form, department: e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-0.5 h-8 text-xs" /></div>
            <Button onClick={submit} className="w-full bg-lime-500 hover:bg-lime-400 text-[#0f172a] font-semibold h-9 text-xs">Tạo workflow</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== AI SUGGESTIONS ====================
function SuggestionsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try { const { data } = await api.post("/operations/ai-suggestions"); setData(data); }
    catch { toast.error("Lỗi"); }
    finally { setLoading(false); }
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4" data-testid="suggestions-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><Brain className="w-5 h-5 text-lime-400" /> AI Gợi ý cải thiện</h2>
        <Button onClick={generate} disabled={loading} className="bg-lime-500 hover:bg-lime-400 text-[#0f172a] gap-1.5 text-xs">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} AI Phân tích
        </Button>
      </div>

      {!data && !loading && (
        <div className="text-center py-12"><Brain className="w-10 h-10 text-slate-600 mx-auto mb-3" /><p className="text-sm text-slate-500">Bấm "AI Phân tích" để xem gợi ý.</p></div>
      )}

      {data && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-3 text-center">
              <p className="text-[9px] text-slate-500">Sức khoẻ đội ngũ</p>
              <p className={`text-2xl font-bold ${data.team_health >= 70 ? "text-emerald-400" : data.team_health >= 40 ? "text-amber-400" : "text-red-400"}`}>{data.team_health}/100</p>
            </div>
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-3 text-center">
              <p className="text-[9px] text-slate-500">Vấn đề lớn nhất</p>
              <p className="text-xs text-red-400 mt-1">{data.top_issue}</p>
            </div>
          </div>

          {data.suggestions?.map((s, i) => (
            <div key={i} className={`rounded-xl p-3 border ${s.type === "warning" ? "bg-amber-500/5 border-amber-500/20" : s.type === "improvement" ? "bg-emerald-500/5 border-emerald-500/20" : "bg-[#1e293b] border-[#334155]"}`}>
              <p className="text-sm font-medium text-slate-200">{s.title}</p>
              <p className="text-xs text-slate-400 mt-0.5">{s.detail}</p>
              {s.action && <p className="text-[10px] text-lime-400 mt-1">Hành động: {s.action}</p>}
            </div>
          ))}

          {data.top_opportunity && (
            <div className="bg-lime-500/5 border border-lime-500/20 rounded-xl p-3">
              <p className="text-[9px] text-lime-400 uppercase">Cơ hội cải thiện</p>
              <p className="text-xs text-slate-200 mt-0.5">{data.top_opportunity}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
